import { useEffect, useState } from 'react';
import { Box, FormControl, FormHelperText, MenuItem, TextField, Typography } from '@material-ui/core';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { LabelWithTooltip, useScaffolderTooltip } from '../LabelWithTooltip';

// -----------------------------------------------------------------------------------
// Hostname + Dominio para el golden path de IaC MVP.
//
// Se renderizan en la MISMA línea porque juntos forman el endpoint final de acceso:
//
//     <hostname base>-<sufijo de entorno> . <dominio>
//     └── editable ──┘└─ NO editable ────┘   └ desplegable ┘
//
// El sufijo de entorno se deriva del `environment` elegido en el paso anterior
// ("Datos obligatorios de la organización") y se muestra FUERA del input, como texto
// fijo, para que quede claro que el usuario no puede modificarlo.
//
// De momento la lista de dominios está "a pelo". En el futuro deberá salir de algún
// sitio donde cada dominio se vincule con algo de la plataforma (p. ej. catálogo,
// config o un backend), sustituyendo la constante DOMAIN_OPTIONS.
// -----------------------------------------------------------------------------------

const DOMAIN_OPTIONS = ['acciona.com', 'acciona.int', 'acciona-construccion.com'];

// Sufijo que se añade al hostname según el entorno del paso 1. PRO NO lleva sufijo
// (convención de plataforma: producción usa el nombre "limpio").
const ENV_HOSTNAME_SUFFIX: Record<string, string> = {
  DEV: 'dev',
  PRE: 'pre',
  PRO: '',
};

// Una etiqueta DNS admite como máximo 63 caracteres. Como el sufijo se añade DESPUÉS
// de lo que teclea el usuario, descontamos del presupuesto el sufijo más largo (más su
// guion) para que el hostname final no pueda pasarse de largo en ningún entorno.
const MAX_DNS_LABEL_LENGTH = 63;
const LONGEST_ENV_SUFFIX = Math.max(
  ...Object.values(ENV_HOSTNAME_SUFFIX).map(suffix => suffix.length),
);
const MAX_BASE_HOSTNAME_LENGTH =
  MAX_DNS_LABEL_LENGTH - (LONGEST_ENV_SUFFIX > 0 ? LONGEST_ENV_SUFFIX + 1 : 0);

// Hostname válido: minúsculas, dígitos y guiones (no puede empezar/terminar en guion).
const HOSTNAME_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

/** Compone la etiqueta de host final a partir de la base y el sufijo de entorno. */
const buildHostLabel = (baseHostname: string, envSuffix: string) => {
  const base = baseHostname.trim();
  if (!base) return '';
  return envSuffix ? `${base}-${envSuffix}` : base;
};

/** Alto aproximado de un input `standard` de MUI, para alinear con él los separadores. */
const INPUT_HEIGHT = 32;

/**
 * Separador literal ("-dev", ".") entre hostname y dominio.
 *
 * Reserva encima un hueco invisible del alto de una etiqueta para que el símbolo quede
 * alineado con los inputs y no con los labels, ahora que estos se pintan por fuera.
 */
const HostSeparator = ({ children }: { children: string }) => (
  <Box>
    <Box aria-hidden="true" style={{ visibility: 'hidden' }}>
      <LabelWithTooltip label="&nbsp;" />
    </Box>
    <Box display="flex" alignItems="center" height={INPUT_HEIGHT}>
      <Typography
        variant="h6"
        component="span"
        color="textSecondary"
        style={{ whiteSpace: 'nowrap' }}
      >
        {children}
      </Typography>
    </Box>
  </Box>
);

export const HostnameDomainField = (props: FieldExtensionComponentProps<any>) => {
  const { onChange, rawErrors, required, formData } = props;

  const hostnameTooltip = useScaffolderTooltip('iacMvp.hostname');
  const domainTooltip = useScaffolderTooltip('iacMvp.domain');

  // El entorno se elige en el paso anterior. Igual que en TemplateSelectorFieldCD /
  // DynamicFormGeneratorFieldCD, los datos de pasos previos llegan por formContext.
  const environment = (props as any).formContext?.formData?.environment ?? '';
  const envSuffix = ENV_HOSTNAME_SUFFIX[environment] ?? '';

  const [baseHostname, setBaseHostname] = useState('');
  const [domain, setDomain] = useState(DOMAIN_OPTIONS[0]);

  // ---------------------------------------------------------------------------------
  // 1. Sincronizar con formData (navegación entre pasos hacia atrás/adelante).
  //    Se lee `baseHostname` (lo que teclea el usuario), NO `hostname`, que ya lleva
  //    el sufijo aplicado y volcarlo al input duplicaría el sufijo.
  // ---------------------------------------------------------------------------------
  useEffect(() => {
    if (!formData) return;
    setBaseHostname(formData.baseHostname || '');
    setDomain(formData.domain || DOMAIN_OPTIONS[0]);
    // Se depende de los dos campos concretos y no de `formData`: su referencia cambia
    // en cada onChange, y resincronizar ahí pisaría lo que el usuario está tecleando.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.baseHostname, formData?.domain]);

  // ---------------------------------------------------------------------------------
  // 2. Derivar y publicar el valor del campo.
  //    Al depender también de `envSuffix`, si el usuario vuelve al paso 1 y cambia el
  //    entorno, el hostname se recalcula solo (silenciosamente) al repintarse el paso.
  // ---------------------------------------------------------------------------------
  useEffect(() => {
    const base = baseHostname.trim();
    const hostLabel = buildHostLabel(base, envSuffix);

    onChange({
      // Lo que teclea el usuario, sin sufijo. Se persiste para poder repoblar el input
      // y para que la validación compruebe la longitud sobre la base.
      baseHostname: base,
      envSuffix,
      // Etiqueta de host final (con sufijo de entorno). Va a la API externa.
      hostname: hostLabel,
      // Dominio. Va a la API externa por separado, no concatenado al hostname.
      domain,
      // Endpoint completo, solo para mostrar y para trazabilidad.
      endpoint: hostLabel ? `${hostLabel}.${domain}` : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseHostname, domain, envSuffix]);

  const error = rawErrors?.[0];
  const endpointPreview = buildHostLabel(baseHostname, envSuffix)
    ? `${buildHostLabel(baseHostname, envSuffix)}.${domain}`
    : '';

  return (
    <FormControl margin="normal" required={required} fullWidth error={!!error}>
      <Box display="flex" alignItems="flex-start" gridGap={8}>
        <Box style={{ flex: '0 1 200px' }}>
          <LabelWithTooltip label="Hostname" tooltip={hostnameTooltip} required />
          <TextField
            value={baseHostname}
            onChange={e => setBaseHostname(e.target.value)}
            error={!!error}
            inputProps={{ maxLength: MAX_BASE_HOSTNAME_LENGTH }}
            helperText={`Minúsculas, dígitos y guiones (máx. ${MAX_BASE_HOSTNAME_LENGTH})`}
            fullWidth
          />
        </Box>

        {/* Sufijo de entorno: fuera del input y no editable. */}
        {envSuffix && <HostSeparator>{`-${envSuffix}`}</HostSeparator>}

        <HostSeparator>.</HostSeparator>

        <Box minWidth={220}>
          <LabelWithTooltip label="Dominio" tooltip={domainTooltip} required />
          <TextField
            select
            value={domain}
            onChange={e => setDomain(e.target.value)}
            helperText="Selecciona el dominio"
            fullWidth
          >
            {DOMAIN_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
      {endpointPreview && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
          Endpoint de acceso: <strong>{endpointPreview}</strong>
        </Typography>
      )}
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

// -----------------------------------------------------------------------------------
// Validación
//
// Nota: esta función solo recibe el valor del propio campo, no el resto del
// formulario, así que valida la BASE del hostname. El sufijo de entorno lo añade el
// componente y su longitud ya está descontada de MAX_BASE_HOSTNAME_LENGTH.
// -----------------------------------------------------------------------------------
export const hostnameDomainValidation = (
  value: { baseHostname?: string; domain?: string },
  validation: FieldValidation,
) => {
  const baseHostname = (value?.baseHostname || '').trim();

  if (!baseHostname) {
    validation.addError('El hostname es obligatorio.');
  } else if (!HOSTNAME_REGEX.test(baseHostname)) {
    validation.addError(
      'El hostname solo puede contener minúsculas, dígitos y guiones (sin empezar ni terminar en guion).',
    );
  } else if (baseHostname.length > MAX_BASE_HOSTNAME_LENGTH) {
    validation.addError(
      `El hostname no puede superar ${MAX_BASE_HOSTNAME_LENGTH} caracteres (se le añade el sufijo del entorno).`,
    );
  }

  if (!value?.domain) {
    validation.addError('El dominio es obligatorio.');
  }
};

// -----------------------------------------------------------------------------------
// Registro del Custom Field
// -----------------------------------------------------------------------------------
export const HostnameDomainCaseFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'HostnameDomainField',
    component: HostnameDomainField,
    validation: hostnameDomainValidation,
  }),
);
